import { Crown, Sparkles, Rocket } from 'lucide-react';
import { Badge } from './ui/badge';

// Founding Exporter Badge - displays on profile cards and listings
export function FoundingExporterBadge({ size = 'default', showTooltip = true }) {
  const sizeClasses = {
    small: 'text-xs px-2 py-0.5',
    default: 'text-sm px-3 py-1',
    large: 'text-base px-4 py-1.5'
  };

  return (
    <Badge 
      className={`
        bg-gradient-to-r from-amber-500 to-orange-500 
        text-white border-0 font-semibold
        ${sizeClasses[size]}
      `}
      title={showTooltip ? "Founding Exporter - Pro plan free for 12 months" : undefined}
    >
      <Crown className={`${size === 'small' ? 'h-3 w-3' : 'h-4 w-4'} mr-1`} />
      Founding Exporter
    </Badge>
  );
}

// Pro Badge - for Pro plan users
export function ProBadge({ size = 'default' }) {
  const sizeClasses = {
    small: 'text-xs px-2 py-0.5',
    default: 'text-sm px-3 py-1',
    large: 'text-base px-4 py-1.5'
  };

  return (
    <Badge 
      className={`
        bg-gradient-to-r from-emerald-500 to-teal-500 
        text-white border-0 font-semibold
        ${sizeClasses[size]}
      `}
    >
      <Sparkles className={`${size === 'small' ? 'h-3 w-3' : 'h-4 w-4'} mr-1`} />
      Pro
    </Badge>
  );
}

// Founding Banner - for homepage and pricing page
export function FoundingBanner({ claimed = 0, total = 500, onClaimClick }) {
  const remaining = total - claimed;
  const progressPercentage = (claimed / total) * 100;

  return (
    <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-amber-500 text-white py-3 px-4">
      <div className="container mx-auto flex flex-col sm:flex-row items-center justify-center gap-2 text-center">
        <Rocket className="h-5 w-5 animate-pulse" />
        <span className="font-semibold">
          First 500 exporters get Pro FREE for 12 months
        </span>
        <span className="hidden sm:inline">•</span>
        <span className="font-bold text-lg">
          {claimed}/{total} claimed
        </span>
        <div className="w-32 h-2 bg-white/30 rounded-full overflow-hidden ml-2">
          <div 
            className="h-full bg-white transition-all duration-500"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
        {onClaimClick && remaining > 0 && (
          <button 
            onClick={onClaimClick}
            className="ml-2 px-3 py-1 bg-white/20 hover:bg-white/30 rounded-full text-sm font-medium transition-colors"
          >
            Claim Now →
          </button>
        )}
      </div>
    </div>
  );
}

// Compact Founding Counter - for sidebar or cards
export function FoundingCounter({ claimed = 0, total = 500, size = 'default' }) {
  const remaining = total - claimed;
  const progressPercentage = (claimed / total) * 100;

  if (size === 'small') {
    return (
      <div className="flex items-center gap-2 text-xs text-slate-400">
        <Rocket className="h-3 w-3 text-amber-400" />
        <span>{remaining} founding spots left</span>
      </div>
    );
  }

  return (
    <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
      <div className="flex items-center justify-between mb-2">
        <span className="text-amber-400 text-sm font-medium flex items-center gap-1">
          <Rocket className="h-4 w-4" />
          Founding Exporter
        </span>
        <span className="text-white text-sm font-bold">
          {claimed}/{total}
        </span>
      </div>
      <div className="w-full h-1.5 bg-slate-700 rounded-full overflow-hidden">
        <div 
          className="h-full bg-gradient-to-r from-amber-500 to-orange-500 transition-all duration-500"
          style={{ width: `${progressPercentage}%` }}
        />
      </div>
      <p className="text-xs text-slate-400 mt-1">
        {remaining} spots remaining for free Pro
      </p>
    </div>
  );
}

// User Plan Badge - shows current plan on profiles
export function UserPlanBadge({ plan, foundingMember, size = 'default' }) {
  if (foundingMember) {
    return <FoundingExporterBadge size={size} />;
  }
  
  if (plan === 'pro') {
    return <ProBadge size={size} />;
  }
  
  if (plan === 'enterprise') {
    return (
      <Badge className={`bg-purple-500/20 text-purple-400 border-purple-500/30 ${size === 'small' ? 'text-xs' : ''}`}>
        Enterprise
      </Badge>
    );
  }
  
  // Starter or no plan
  return null;
}

export default FoundingExporterBadge;
